import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { shopModeApi, type FloorSellableItem } from "../../api/shopMode";
import { t } from "../../i18n/t";
import type { TicketLine } from "./TicketLines";

/**
 * The finder-side state every floor ticket host needs — ONE implementation
 * (code-review 2026-08-09: the inline sell and the kiosk order form had
 * verbatim copies, and both latched a FAILED load forever):
 *
 * - the stock list, loaded lazily the first time `active` is true, latched
 *   only on SUCCESS — a failed load retries the next time `active` turns on;
 * - `invalidateStock()` for the host to call after a sale, so the next
 *   activation refetches the quantities that sale just changed;
 * - the ticket lines and the ONE pick rule: increment up to `qtyOnHand`,
 *   else add at qty 1 with the worked-out price prefilled.
 */
export function useStockTicket(active: boolean) {
  const [lines, setLines] = useState<TicketLine[]>([]);
  const [stock, setStock] = useState<FloorSellableItem[]>([]);
  const [stockLoading, setStockLoading] = useState(false);
  const [stockError, setStockError] = useState<string | null>(null);
  const loadedRef = useRef(false);
  const loadingRef = useRef(false);

  useEffect(() => {
    if (!active || loadedRef.current || loadingRef.current) return;
    loadingRef.current = true;
    setStockLoading(true);
    setStockError(null);
    void shopModeApi
      .floorListSellableItemsInStock()
      .then((items) => {
        loadedRef.current = true;
        setStock(items);
      })
      .catch(() => setStockError(t("ticket.couldNotLoadProducts")))
      .finally(() => {
        loadingRef.current = false;
        setStockLoading(false);
      });
  }, [active]);

  /** Call after a completed sale: those quantities are stale now. */
  const invalidateStock = useCallback(() => {
    loadedRef.current = false;
  }, []);

  const qtyInTicket = useMemo(() => {
    const map = new Map<string, number>();
    for (const line of lines) {
      map.set(
        line.item.id,
        (map.get(line.item.id) ?? 0) + (Number(line.qtyStr) || 0),
      );
    }
    return map;
  }, [lines]);

  const pickItem = useCallback((item: FloorSellableItem) => {
    setLines((cur) => {
      const existing = cur.find((l) => l.item.id === item.id);
      if (existing) {
        const next = Number(existing.qtyStr) || 0;
        if (next >= Number(item.qtyOnHand)) return cur;
        return cur.map((l) =>
          l.item.id === item.id ? { ...l, qtyStr: String(next + 1) } : l,
        );
      }
      return [...cur, { item, qtyStr: "1", priceStr: item.saleUnitPriceEur ?? "" }];
    });
  }, []);

  return {
    lines,
    setLines,
    stock,
    stockLoading,
    stockError,
    qtyInTicket,
    pickItem,
    invalidateStock,
  };
}
